<template>
  <section 
    class="layout-pt-lg layout-pb-lg "
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div class="container tw-flex tw-flex-col tw-gap-6 tw-text-black lg:tw-gap-8 tw-h-full">
      <!-- Mobile Layout -->
      <div class="tw-flex tw-flex-col tw-gap-4 lg:tw-hidden">
        <!-- First Image -->
        <div class="tw-w-full tw-relative">
          <!-- Sol border -->
          <div 
            class="tw-absolute tw-left-0 tw-top-4 tw-w-1 tw-h-[calc(100%-2rem)] tw-z-10"
            :style="{ backgroundColor: 'var(--c-secondary)' }"
          ></div>
          <!-- Alt border -->
          <div 
            class="tw-absolute tw-left-0 tw-bottom-0 tw-w-[calc(100%-2rem)] tw-h-1 tw-z-10"
            :style="{ backgroundColor: 'var(--c-secondary)' }"
          ></div>
          <NuxtImg 
            v-if="vars.photoImage"
            :src="vars.photoImage"
            class="tw-h-auto tw-w-full"
          />
        </div>

        <!-- Title and Description -->
        <div>
          <h6
            v-if="vars.altTitleText"
            style="color: var(--c-secondary) !important;" 
            class="tw-mb-3"
          >
            {{ vars.altTitleText }}
          </h6>
          <h2 class="tw-text-2xl tw-font-bold tw-mb-4">{{ vars.titleText }}</h2>
          
          <div v-if="vars.descriptionText" class="tw-mb-6">
            <p 
              class="tw-text-base tw-leading-relaxed"
              :style="{ color: `var(--c-${vars.textColor})` }"
            >
              {{ vars.descriptionText }}
            </p>
          </div>
        </div>

        <!-- Second Image -->
        <div v-if="vars.thumbnailImage" class="tw-w-full">
          <NuxtImg
            :src="vars.thumbnailImage"
            class="tw-h-80 tw-w-full tw-object-cover"
          />
        </div>

        <!-- Secondary Title and Descriptions -->
        <div>
          <h6 
            v-if="vars.secondaryTitleText"
            class="tw-mb-6" 
            style="color: var(--c-secondary) !important;"
          >
            {{ vars.secondaryTitleText }}
          </h6>
          
          <div
            v-for="(item, idx) in descriptions"
            :key="`mobile-desc-${idx}`"
            class="tw-flex tw-items-center tw-gap-3"  
          >
            <span class="tw-flex tw-items-center" style="color: var(--c-secondary) !important;">
              <svg
                class="align-middle"                    
                width="5" 
                height="9" 
                viewBox="0 0 5 9" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.550315 0L5 4.5L0.550315 9L0 8.43551L3.89151 4.5L0 0.564488L0.550315 0Z"
                  fill="#616161"
                />
              </svg>
            </span>
            <p :style="{ color: `var(--c-${vars.textColor})` }">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>

      <!-- Desktop Layout -->
      <div class="tw-hidden lg:tw-flex tw-flex-col tw-h-full tw-gap-6">
        <!-- Top - Images Section -->
        <div class="tw-relative tw-h-[min(60vh,500px)] tw-w-full tw-max-w-6xl tw-mx-auto">
          <!-- Large Image -->
          <div class="tw-w-3/4 tw-h-5/6 -tw-bottom-10 tw-right-12 tw-z-20 tw-relative">
            <!-- Sol border -->
            <div 
              class="tw-absolute tw-left-0 tw-top-8 tw-w-2 tw-h-[calc(100%-2rem)] tw-z-10"
              :style="{ backgroundColor: 'var(--c-secondary)' }"
            ></div>
            <!-- Alt border -->
            <div 
              class="tw-absolute tw-left-0 tw-bottom-0 tw-w-[calc(100%-2rem)] tw-h-2 tw-z-10"
              :style="{ backgroundColor: 'var(--c-secondary)' }"
            ></div>
            <NuxtImg 
              v-if="vars.photoImage"
              :src="vars.photoImage"
              class="tw-h-full tw-w-full tw-object-cover"
            />
          </div>
          
          <!-- Small Image - Overlapping on right -->
          <div 
            class="tw-absolute tw-right-0 -tw-bottom-6 tw-w-2/5 tw-h-4/5 tw-z-10" 
            v-if="vars.thumbnailImage"
          >
            <NuxtImg
              :src="vars.thumbnailImage"
              class="tw-h-full tw-w-full tw-object-cover"
            />
          </div>
        </div>

        <!-- Bottom - Content Section -->
        <div class="tw-h-[min(40vh,400px)] tw-pt-8">
          <!-- Content Grid -->
          <div class="tw-grid tw-grid-cols-2 tw-gap-16">
            <!-- Left Column - Main Description -->
            <div>
              <h6
                v-if="vars.altTitleText"
                style="color: var(--c-secondary) !important;" 
                class="tw-mb-3"
              >
                {{ vars.altTitleText }}
              </h6>
              <h2 class="tw-text-4xl tw-font-bold tw-leading-tight tw-mb-8 tw-text-left">{{ vars.titleText }}</h2>
              <p 
                v-if="vars.descriptionText"
                class="tw-text-base tw-leading-relaxed"
                :style="{ color: `var(--c-${vars.textColor})` }"
              >
                {{ vars.descriptionText }}
              </p>
            </div>

            <!-- Right Column - Secondary Content -->
            <div>
              <!-- Secondary Title -->
              <h3 
                v-if="vars.secondaryTitleText"
                class="tw-mb-4" 
                style="color: var(--c-secondary) !important;"
              >
                {{ vars.secondaryTitleText }}
              </h3>
              
              <!-- Descriptions List -->
              <div v-if="descriptions" class="tw-flex tw-flex-col tw-gap-3">
                <div
                  v-for="(item, idx) in descriptions"
                  :key="`desktop-desc-${idx}`"
                  class="tw-flex tw-items-center tw-gap-3"  
                >
                  <span class="tw-flex tw-items-center" style="color: var(--c-secondary) !important;">
                    <svg
                      class="align-middle"                    
                      width="5" 
                      height="9" 
                      viewBox="0 0 5 9" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.550315 0L5 4.5L0.550315 9L0 8.43551L3.89151 4.5L0 0.564488L0.550315 0Z"
                        fill="#616161"
                      />
                    </svg>
                  </span>
                  <p
                    :style="{ color: `var(--c-${vars.textColor})` }"
                    class="tw-leading-snug"              
                  >
                    {{ item.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
export default defineNuxtComponent({
  mixins: [ComponentMixin],

  data() {
    return {
      isVisible: false,
      
    };
  },

  computed: {
    descriptions() {
      return this.groupedVariables.descriptions;
    },
     items() {
      return this.groupedVariables.items;
    },
  },

  methods: {
    isExternalLink(url: string) {
      return url.includes('http');
    },
  },
});
</script>
